import { telephonyApi, userAudioRecordingApi } from 'app/services/api';
import { getTelephonyTransport } from 'app/services/telephony/transport';
import {
  TELEPHONY_CONNECTION_STATE,
  TELEPHONY_TRANSPORT_EVENT,
} from 'app/services/telephony/transport/telephonyTransport.constants';
import { unwrapTelnyxCredentialJwt } from 'app/services/telnyx/TelnyxProvider.utils';
import telnyxService from 'app/services/telnyx/telnyxService';
import _ from 'lodash';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { TelephonySessionKind, UserStatus } from 'constants/telephony';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { reportNativeError, reportNativeEvent } from 'utils/native/nativeTelemetry';
import { getIsNativePlatform } from 'utils/platform';

import { showErrorMessageAction } from 'store/global/actions';
import { selectCanPlaceCall } from 'store/system/selectors/telephony.selectors';

import { getOrCreateNativeDeviceId } from './telephony.actions.utils';

const USER_STATUS_STORAGE_KEY = 'telephony_user_status';

export const TELEPHONY_ACTION_TYPE = createNamedActions('telephony', {
  SET_WIDGET_OPEN: 'SET_WIDGET_OPEN',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  FETCH_USER_PENDING: 'FETCH_USER_PENDING',
  FETCH_USER_FULFILLED: 'FETCH_USER_FULFILLED',
  FETCH_USER_REJECTED: 'FETCH_USER_REJECTED',
  UPDATE_USER_STATUS: 'UPDATE_USER_STATUS',
  TELNYX_CONNECTING: 'TELNYX_CONNECTING',
  TELNYX_CONNECTED: 'TELNYX_CONNECTED',
  TELNYX_DISCONNECTED: 'TELNYX_DISCONNECTED',
  TELNYX_ERROR: 'TELNYX_ERROR',
  CALL_OUTGOING: 'CALL_OUTGOING',
  CALL_INCOMING: 'CALL_INCOMING',
  CALL_ACTIVE: 'CALL_ACTIVE',
  CALL_HANGUP: 'CALL_HANGUP',
  CALL_DESTROY: 'CALL_DESTROY',
  CALL_STATE_UPDATE: 'CALL_STATE_UPDATE',
  CALL_HOLD: 'CALL_HOLD',
  CALL_UNHOLD: 'CALL_UNHOLD',
  CALL_MUTE: 'CALL_MUTE',
  CALL_UNMUTE: 'CALL_UNMUTE',
  CALL_RESET: 'CALL_RESET',
  SET_DIALED_NUMBER: 'SET_DIALED_NUMBER',
  FETCH_CONTACTS_PENDING: 'FETCH_CONTACTS_PENDING',
  FETCH_CONTACTS_FULFILLED: 'FETCH_CONTACTS_FULFILLED',
  FETCH_CONTACTS_REJECTED: 'FETCH_CONTACTS_REJECTED',
  SET_CONTACTS_SEARCH: 'SET_CONTACTS_SEARCH',
  UPDATE_CONTACT_STATUS: 'UPDATE_CONTACT_STATUS',
  FETCH_CALL_HISTORY_PENDING: 'FETCH_CALL_HISTORY_PENDING',
  FETCH_CALL_HISTORY_FULFILLED: 'FETCH_CALL_HISTORY_FULFILLED',
  FETCH_CALL_HISTORY_REJECTED: 'FETCH_CALL_HISTORY_REJECTED',
  SET_CALL_HISTORY_FILTER: 'SET_CALL_HISTORY_FILTER',
  // SignalR connection state
  SIGNALR_CONNECTING: 'SIGNALR_CONNECTING',
  SIGNALR_CONNECTED: 'SIGNALR_CONNECTED',
  SIGNALR_DISCONNECTED: 'SIGNALR_DISCONNECTED',
  SIGNALR_RECONNECTING: 'SIGNALR_RECONNECTING',
  // Lead notification
  SHOW_LEAD_NOTIFICATION: 'SHOW_LEAD_NOTIFICATION',
  DISMISS_LEAD_NOTIFICATION: 'DISMISS_LEAD_NOTIFICATION',
  ACCEPT_LEAD_PENDING: 'ACCEPT_LEAD_PENDING',
  ACCEPT_LEAD_FULFILLED: 'ACCEPT_LEAD_FULFILLED',
  ACCEPT_LEAD_REJECTED: 'ACCEPT_LEAD_REJECTED',
  REJECT_LEAD_PENDING: 'REJECT_LEAD_PENDING',
  REJECT_LEAD_FULFILLED: 'REJECT_LEAD_FULFILLED',
  REJECT_LEAD_REJECTED: 'REJECT_LEAD_REJECTED',
  AUTO_REJECT_LEAD: 'AUTO_REJECT_LEAD',
  LEAD_NOTIFICATION_TIMEOUT: 'LEAD_NOTIFICATION_TIMEOUT',
  // SignalR events
  TEXT_MESSAGE_RECEIVED: 'TEXT_MESSAGE_RECEIVED',
  WEB_CHAT_MESSAGE_RECEIVED: 'WEB_CHAT_MESSAGE_RECEIVED',
  CALL_FAILED: 'CALL_FAILED',
  CONFERENCE_PARTICIPANT_JOINING: 'CONFERENCE_PARTICIPANT_JOINING',
  CONFERENCE_PARTICIPANT_JOINED: 'CONFERENCE_PARTICIPANT_JOINED',
  // Text messaging (DEV-1453)
  FETCH_TEXT_LEADS_PENDING: 'FETCH_TEXT_LEADS_PENDING',
  FETCH_TEXT_LEADS_FULFILLED: 'FETCH_TEXT_LEADS_FULFILLED',
  FETCH_TEXT_LEADS_REJECTED: 'FETCH_TEXT_LEADS_REJECTED',
  SET_TEXT_LEADS_SEARCH: 'SET_TEXT_LEADS_SEARCH',
  SET_ACTIVE_CONVERSATION: 'SET_ACTIVE_CONVERSATION',
  FETCH_TEXT_MESSAGES_PENDING: 'FETCH_TEXT_MESSAGES_PENDING',
  FETCH_TEXT_MESSAGES_FULFILLED: 'FETCH_TEXT_MESSAGES_FULFILLED',
  FETCH_TEXT_MESSAGES_REJECTED: 'FETCH_TEXT_MESSAGES_REJECTED',
  SEND_TEXT_MESSAGE_PENDING: 'SEND_TEXT_MESSAGE_PENDING',
  SEND_TEXT_MESSAGE_FULFILLED: 'SEND_TEXT_MESSAGE_FULFILLED',
  SEND_TEXT_MESSAGE_REJECTED: 'SEND_TEXT_MESSAGE_REJECTED',
  CLEAR_ACTIVE_CONVERSATION: 'CLEAR_ACTIVE_CONVERSATION',
  // Borrower chat (DEV-1454)
  SET_BORROWER_CHAT_INITIALIZED: 'SET_BORROWER_CHAT_INITIALIZED',
  SET_BORROWER_CHAT_UNREAD_COUNT: 'SET_BORROWER_CHAT_UNREAD_COUNT',
  SET_BORROWER_CHAT_ACTIVE_CHANNEL: 'SET_BORROWER_CHAT_ACTIVE_CHANNEL',
  // Internal chat (DEV-1455)
  SET_INTERNAL_CHAT_UNREAD_COUNT: 'SET_INTERNAL_CHAT_UNREAD_COUNT',
  SET_INTERNAL_CHAT_ACTIVE_CHANNEL: 'SET_INTERNAL_CHAT_ACTIVE_CHANNEL',
  // Audio device settings (DEV-1482)
  SET_AVAILABLE_AUDIO_DEVICES: 'SET_AVAILABLE_AUDIO_DEVICES',
  SET_SELECTED_INPUT_DEVICE: 'SET_SELECTED_INPUT_DEVICE',
  SET_SELECTED_OUTPUT_DEVICE: 'SET_SELECTED_OUTPUT_DEVICE',
  SET_AUDIO_PERMISSION_STATE: 'SET_AUDIO_PERMISSION_STATE',
  // Voicemail greeting (DEV-1575)
  FETCH_ACTIVE_GREETING_PENDING: 'FETCH_ACTIVE_GREETING_PENDING',
  FETCH_ACTIVE_GREETING_FULFILLED: 'FETCH_ACTIVE_GREETING_FULFILLED',
  FETCH_ACTIVE_GREETING_REJECTED: 'FETCH_ACTIVE_GREETING_REJECTED',
  SET_ACTIVE_GREETING_PENDING: 'SET_ACTIVE_GREETING_PENDING',
  SET_ACTIVE_GREETING_FULFILLED: 'SET_ACTIVE_GREETING_FULFILLED',
  SET_ACTIVE_GREETING_REJECTED: 'SET_ACTIVE_GREETING_REJECTED',
  REMOVE_ACTIVE_GREETING_PENDING: 'REMOVE_ACTIVE_GREETING_PENDING',
  REMOVE_ACTIVE_GREETING_FULFILLED: 'REMOVE_ACTIVE_GREETING_FULFILLED',
  REMOVE_ACTIVE_GREETING_REJECTED: 'REMOVE_ACTIVE_GREETING_REJECTED',
  // Voicemail inbox (DEV-1577)
  FETCH_VOICEMAILS_PENDING: 'FETCH_VOICEMAILS_PENDING',
  FETCH_VOICEMAILS_FULFILLED: 'FETCH_VOICEMAILS_FULFILLED',
  FETCH_VOICEMAILS_REJECTED: 'FETCH_VOICEMAILS_REJECTED',
  FETCH_VOICEMAIL_UNREAD_COUNT_FULFILLED: 'FETCH_VOICEMAIL_UNREAD_COUNT_FULFILLED',
  MARK_VOICEMAIL_READ_FULFILLED: 'MARK_VOICEMAIL_READ_FULFILLED',
  MARK_ALL_VOICEMAILS_READ_FULFILLED: 'MARK_ALL_VOICEMAILS_READ_FULFILLED',
  DELETE_VOICEMAIL_FULFILLED: 'DELETE_VOICEMAIL_FULFILLED',
  VOICEMAIL_RECEIVED: 'VOICEMAIL_RECEIVED',
});

export const setTelephonyWidgetOpenAction = isOpen => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_WIDGET_OPEN, payload: isOpen });

export const setTelephonyActiveTabAction = tab => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_ACTIVE_TAB, payload: tab });

// Read the last user-selected status from localStorage.  Returns a valid
// UserStatus string or null when nothing is persisted / localStorage is
// unavailable.
const getPersistedUserStatus = () => {
  try {
    const saved = localStorage.getItem(USER_STATUS_STORAGE_KEY);

    return saved && Object.values(UserStatus).includes(saved) ? saved : null;
  } catch {
    return null;
  }
};

// After a page reload the backend will have set the user to Unavailable
// (because the SignalR disconnect was detected).  We need to restore the
// last status the user explicitly chose — or default to Available — and
// sync that back to the backend.
const reconcileStatusAfterReload = async dispatch => {
  const savedStatus = getPersistedUserStatus();

  // Use the persisted status, but if it was explicitly Unavailable keep it.
  // If nothing was saved, default to Available.
  const desiredStatus = savedStatus ?? UserStatus.AVAILABLE;

  if (desiredStatus === UserStatus.UNAVAILABLE) {
    return; // user intentionally went unavailable — respect that
  }

  logger.info(`[Telephony] Restoring status after reload: ${desiredStatus}`);
  dispatch({ type: TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS, payload: desiredStatus });

  try {
    await telephonyApi.updateUserStatus({ contactStatus: desiredStatus });
  } catch (err) {
    logger.warn('[Telephony] Failed to sync restored status to backend', err);
  }
};

export const fetchTelephonyUserAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_USER_PENDING });

  try {
    const { data, status } = await telephonyApi.getTelephonyUser();

    // 204 = telephony-enabled user with no provisioned SIP extension. Leave the widget
    // uninitialized (no user, no status reconcile, no error toast); the Telnyx softphone
    // separately self-disables on its own not-provisioned (404) JWT response.
    if (status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT) {
      dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_USER_REJECTED });

      return null;
    }

    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_USER_FULFILLED, payload: data });

    // The backend sets the user to Unavailable (or keeps stale ON_CALL) when
    // the browser disconnects.  Reconcile with the last user-selected status.
    // Use !== AVAILABLE so we also handle numeric enum values from the backend.
    if (data?.contactStatus !== UserStatus.AVAILABLE) {
      await reconcileStatusAfterReload(dispatch);
    }

    return data;
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_USER_REJECTED });

    // Backend fetch failed — still restore the last known status so the UI
    // doesn't show Unavailable.
    await reconcileStatusAfterReload(dispatch);

    dispatch(showErrorMessageAction({ message: 'Failed to load telephony user', err: error }));
    throw error;
  }
});

export const updateUserStatusAction = catchAsyncActionError(
  ({ contactStatus }) =>
    async (dispatch, getState) => {
      const previousStatus = getState().system.telephony.telephonyUser.user?.contactStatus;

      dispatch({ type: TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS, payload: contactStatus });

      // Persist to localStorage so the status survives page reloads even if
      // the backend /api/call/userInfo fetch fails (e.g. returns 400).
      try {
        localStorage.setItem(USER_STATUS_STORAGE_KEY, contactStatus);
      } catch {
        // localStorage unavailable — ignore
      }

      try {
        await telephonyApi.updateUserStatus({ contactStatus });
      } catch (error) {
        if (previousStatus !== undefined) {
          dispatch({ type: TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS, payload: previousStatus });

          try {
            localStorage.setItem(USER_STATUS_STORAGE_KEY, previousStatus);
          } catch {
            // localStorage unavailable — ignore
          }
        }

        dispatch(showErrorMessageAction({ message: 'Failed to update status', err: error }));
        throw error;
      }
    },
);

// Native telephony bootstrap (DEV-3946, bare Telnyx SDK pattern DEV-4332). On web the @telnyx/react-client
// provider drives connection + call events into Redux; on native that provider is absent (TelephonyHost
// renders bare children), so this wires the NativeCalling plugin transport: bind its events → Redux,
// register the VoIP push token, and connect + log in the TxClient once on boot/login.
//
// BARE PATTERN (DEV-4332): connect on foreground (boot/login); the Telnyx SDK (TxConfig `reconnectClient: true`)
// + the VoIP push own connection, reconnection and state. There is NO reconnect-on-resume keeper and NO
// persistent "stay registered for the app lifetime" churn — those layered extras fought the SDK state machine.
// The VoIP PUSH TOKEN is registered before the boot connect (so the connect carries it) and only dropped on
// logout. A cold-wake (app killed) connect is handled natively (VoIP push → processVoIPNotification).
//
// unsubscribers: cleanup fns for all transport.on() bindings across the current session.
// autoAnswerCallId: the callId from a cold-wake push that should be answered automatically when
//   the incomingCall event fires — consumed (set to null) by the handler on first match.
// connectPromise: the in-flight connect, so a re-fired boot connect (provider re-mount / claims-gate flicker)
//   coalesces onto ONE login instead of opening two sockets (cleared when it settles).
const nativeTelephonyRefs = {
  unsubscribers: [],
  autoAnswerCallId: null,
  connectPromise: null,
  // Set once the per-session endpoint 404s (older backend deploy, or toggled off) — permanent for
  // the app's lifetime so every later mint (boot, cold-wake) skips straight to the legacy path.
  useLegacySessionEndpoint: false,
};

// DEV-6051: mints a per-session WebRTC credential keyed by this device's stable id, mirroring the
// web TelnyxProvider's per-tab session mint. Shared by the boot connect and the cold-wake
// connectFromPush path so both carry the same fallback state.
const mintNativeTelnyxCredential = async () => {
  if (nativeTelephonyRefs.useLegacySessionEndpoint) {
    const { data } = await telephonyApi.getTelnyxJwt();

    return data;
  }

  try {
    const { data } = await telephonyApi.createTelnyxSession({
      sessionId: getOrCreateNativeDeviceId(),
      kind: TelephonySessionKind.MOBILE_DEVICE,
    });

    return data;
  } catch (error) {
    if (error?.response?.status !== HTTP_RESPONSE_STATUS_CODES.NOT_FOUND) {
      throw error;
    }

    nativeTelephonyRefs.useLegacySessionEndpoint = true;
    logger.warn(
      '[NativeTelephony] /api/telnyx/session not found — falling back to legacy JWT endpoint',
    );

    const { data } = await telephonyApi.getTelnyxJwt();

    return data;
  }
};

// The SINGLE connect entry point (DEV-4332). boot (and any future trigger) funnels through here so at most one
// login is in flight at a time. Reuse semantics:
//   - a connect already in flight  -> reuse that promise (no second login);
//   - already connected            -> no-op;
//   - otherwise                    -> one fresh login.
// `source` tags the call site. A connect failure is surfaced as TELNYX_ERROR by refreshAndLoginNative; callers
// swallow the rejection.
const connectNativeTelephony = (dispatch, getState, source) => {
  if (!_.isNil(nativeTelephonyRefs.connectPromise)) {
    reportNativeEvent('NativeCalling.connectReused', { source, reason: 'inFlight' });

    return nativeTelephonyRefs.connectPromise;
  }

  if (getState().system.telephony.telnyx.isConnected) {
    reportNativeEvent('NativeCalling.connectReused', { source, reason: 'alreadyConnected' });

    return Promise.resolve();
  }

  const transport = getTelephonyTransport();

  nativeTelephonyRefs.connectPromise = refreshAndLoginNative(dispatch, transport, source).finally(
    () => {
      nativeTelephonyRefs.connectPromise = null;
    },
  );

  return nativeTelephonyRefs.connectPromise;
};

// Map the plugin's connection state to the Redux action (avoids an if/else chain in the event handler).
const NATIVE_CONNECTION_STATE_ACTION = {
  [TELEPHONY_CONNECTION_STATE.CONNECTED]: { type: TELEPHONY_ACTION_TYPE.TELNYX_CONNECTED },
  [TELEPHONY_CONNECTION_STATE.DISCONNECTED]: { type: TELEPHONY_ACTION_TYPE.TELNYX_DISCONNECTED },
  [TELEPHONY_CONNECTION_STATE.CONNECTING]: { type: TELEPHONY_ACTION_TYPE.TELNYX_CONNECTING },
  [TELEPHONY_CONNECTION_STATE.ERROR]: {
    type: TELEPHONY_ACTION_TYPE.TELNYX_ERROR,
    payload: 'Telephony connection error',
  },
};

const refreshAndLoginNative = async (dispatch, transport, source) => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.TELNYX_CONNECTING });
  reportNativeEvent('NativeCalling.loginAttempt', { source });

  try {
    // mintNativeTelnyxCredential() resolves the axios response DATA, which is an envelope
    // ({ jwt, sipUsername, expiresAt }) for /api/telnyx/session, not a bare JWT string — mirrors the
    // web TelnyxProvider's mint. unwrapTelnyxCredentialJwt shares the unwrap so both platforms read
    // the same key (DEV-6069): the previous raw-object passthrough here meant the native client could
    // never authenticate — onClientReady never fired, the dialer stayed gated ("nothing happens"), and
    // pre-ready dials threw ("unknown error").
    const data = await mintNativeTelnyxCredential();
    const token = unwrapTelnyxCredentialJwt(data);

    if (!_.isString(token) || !token) {
      throw new Error(`telnyx jwt not a non-empty string (type=${typeof token})`);
    }

    // The native plugin resolves login() only when the Telnyx client is READY (gateway-registered,
    // i.e. callable) — see TelnyxCallEngine.login — so this resolution is the authoritative "connected"
    // signal, and a call placed after it succeeds. The plugin also emits CONNECTION_STATE_CHANGED (bound
    // in initNativeTelephonyAction), which keeps the state correct if the socket later drops/errors.
    await transport.login({ token });

    dispatch({ type: TELEPHONY_ACTION_TYPE.TELNYX_CONNECTED });
    reportNativeEvent('NativeCalling.loginResult', { ok: true, source });
  } catch (error) {
    logger.error('[NativeTelephony] login failed', error);
    // Surface the real failure reason to App Insights (the browser SDK is off on native) so a connect
    // failure is diagnosable without a device — the bare TELNYX_ERROR state alone isn't.
    reportNativeEvent('NativeCalling.loginResult', { ok: false, source });
    reportNativeError(error, { phase: 'nativeTelephonyLogin' });
    dispatch({
      type: TELEPHONY_ACTION_TYPE.TELNYX_ERROR,
      payload: 'Failed to connect to telephony',
    });
    // Re-throw so connectNativeTelephony (boot) rejects: the boot caller swallows it (best-effort) but it still
    // settles the dedup promise and leaves the state as TELNYX_ERROR rather than a silent half-connected client.
    throw error;
  }
};

// Mounted once on native (NativeTelephonyProvider). Binds the plugin transport events → Redux, registers the
// always-on VoIP push token, and CONNECTS + LOGS IN the TxClient once on boot/login (DEV-4332 bare pattern —
// the SDK's `reconnectClient: true` + the VoIP push own reconnection thereafter; no resume keeper).
export const initNativeTelephonyAction = () => async (dispatch, getState) => {
  if (!getIsNativePlatform()) {
    return;
  }

  const transport = getTelephonyTransport();

  // Idempotent: drop any prior subscriptions before re-binding.
  nativeTelephonyRefs.unsubscribers.forEach(off => off());
  nativeTelephonyRefs.unsubscribers = [
    transport.on(TELEPHONY_TRANSPORT_EVENT.CONNECTION_STATE_CHANGED, ({ state } = {}) =>
      dispatch(
        NATIVE_CONNECTION_STATE_ACTION[state] ?? {
          type: TELEPHONY_ACTION_TYPE.TELNYX_CONNECTING,
        },
      ),
    ),
    transport.on(
      TELEPHONY_TRANSPORT_EVENT.INCOMING_CALL,
      ({ callId, callerName, callerNumber } = {}) => {
        dispatch({
          type: TELEPHONY_ACTION_TYPE.CALL_INCOMING,
          payload: { callId, callerName, callerNumber },
        });

        // Cold-wake auto-answer: consumed once (one-shot guard keyed on callId).
        if (
          !_.isNil(nativeTelephonyRefs.autoAnswerCallId) &&
          nativeTelephonyRefs.autoAnswerCallId === callId
        ) {
          nativeTelephonyRefs.autoAnswerCallId = null;
          transport.answer();
        }
      },
    ),
    transport.on(TELEPHONY_TRANSPORT_EVENT.CALL_ANSWERED, ({ callId } = {}) =>
      // startTime drives the call-duration timer (the reducer keeps `state.call.startTime ?? payload.startTime`,
      // and the web TelnyxProvider sets it on CALL_ACTIVE). Without it the native call timer never ticks.
      dispatch({
        type: TELEPHONY_ACTION_TYPE.CALL_ACTIVE,
        payload: { callId, startTime: Date.now() },
      }),
    ),
    transport.on(TELEPHONY_TRANSPORT_EVENT.CALL_ENDED, ({ cause } = {}) => {
      dispatch({
        type: TELEPHONY_ACTION_TYPE.CALL_HANGUP,
        payload: { cause: cause || 'remote_hangup' },
      });
      // Bare pattern (DEV-4332): the call is over; the native side releases the Call but the SDK keeps its
      // connection (no disconnect on call end). Flush the native audio-session diagnostics buffer to App
      // Insights now the call has ended (reported via the pull path, not the live channel, so cold-wake
      // diagnostics aren't lost).
      transport.drainAudioDiagnostics();
    }),
    transport.on(TELEPHONY_TRANSPORT_EVENT.CALL_STATE_CHANGED, ({ telnyxCallState } = {}) =>
      dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_STATE_UPDATE, payload: { telnyxCallState } }),
    ),
    // The plugin emits the device's VoIP (PushKit) token; feed it to the Telnyx SDK (via the native
    // registerPushToken bridge) so the SDK's login carries pushDeviceToken and Telnyx can send the VoIP
    // push itself (DEV-4332 — replaces the retired Azure-NH deviceTelemetry.registerVoipToken path).
    transport.on(TELEPHONY_TRANSPORT_EVENT.VOIP_PUSH_TOKEN, ({ token, platform } = {}) =>
      transport.registerPushToken({ token, platform }),
    ),
  ];

  // Drain any audio-session diagnostics the native side buffered before this listener bound: a cold-wake
  // call's setup emits them before the WebView mounts, so the live event channel misses them. The buffered
  // pull is the authoritative App Insights channel for them (DEV-4332). Fire-and-forget (never throws).
  transport.drainAudioDiagnostics();

  // Register the VoIP push token (the doorbell) BEFORE the boot connect so the connect carries it. PushKit
  // delivers the token to the native layer before the JS listener binds on cold launch, so the event-based path
  // drops it; pulling here on mount closes that gap and handles token rotation. This is the inbound wake
  // mechanism that survives a backgrounded/killed app.
  try {
    const voip = await transport.getVoipToken();
    const hasToken = !_.isNil(voip?.token) && voip.token !== '';

    // Log the PushKit VoIP token so it can be retrieved from App Insights to send a DIRECT test VoIP
    // push (Telnyx's push-notification tool) — isolating the APNS/cert/token path from Telnyx's call
    // routing when a backgrounded/killed device isn't waking (DEV-4332). It's a device push token, not a
    // credential; dev-only debugging aid.
    reportNativeEvent('NativeCalling.voipTokenPull', {
      hasToken,
      token: hasToken ? voip.token : null,
      platform: voip?.platform ?? null,
    });

    if (hasToken) {
      await transport.registerPushToken({ token: voip.token, platform: voip.platform });
      reportNativeEvent('NativeCalling.voipRegistered');
    }
  } catch (error) {
    logger.error('[NativeTelephony] getVoipToken failed', error);
    reportNativeError(error, { phase: 'voipTokenPull' });
  }

  // Connect + log in the TxClient once on boot/login (DEV-4332 bare pattern). connectNativeTelephony is
  // single-flight + reuse-guarded, so even if init runs twice (provider re-mount / claims-gate flicker) this is
  // a no-op the second time. Reconnection after a background socket drop is owned by the SDK
  // (`reconnectClient: true`) and the VoIP push — there is no reconnect-on-resume keeper. Best-effort: a failure
  // is surfaced as TELNYX_ERROR; mount must never throw. The cold-wake block below reuses this live session.
  await connectNativeTelephony(dispatch, getState, 'boot').catch(error =>
    logger.error('[NativeTelephony] boot connect failed', error),
  );

  // Cold-wake inbound connect (Android FCM): when the app was cold-started by tapping the full-screen
  // incoming-call notification, the native plugin stashes the call extras in the launch intent. Consume
  // them here and connect on demand via connectFromPush so the pushed call is delivered. (iOS handles its
  // VoIP-push wake entirely natively in prepareForPushedCall, so getPendingPushCall returns null there.)
  // Best-effort: a failure falls back to the normal path (the inbound INVITE still arrives).
  try {
    const pending = await transport.getPendingPushCall();

    if (!_.isNil(pending)) {
      const data = await mintNativeTelnyxCredential();
      const token = unwrapTelnyxCredentialJwt(data);

      // Set the one-shot guard BEFORE connectFromPush so the incomingCall handler
      // can consume it when the native plugin fires the event during reconnect.
      if (pending.autoAnswer) {
        nativeTelephonyRefs.autoAnswerCallId = pending.callId;
      }

      await transport.connectFromPush({
        token,
        callId: pending.callId,
        callerName: pending.callerName,
        callerNumber: pending.callerNumber,
      });
    }
  } catch (error) {
    logger.error('[NativeTelephony] cold-wake connectFromPush failed', error);
    reportNativeError(error, { phase: 'coldWakeConnect' });
    // Non-fatal: the normal INVITE path (push notification tap → Telnyx INVITE) still works.
  }
};

// Logout / unmount teardown. Unbinds the transport subscriptions, drops the SIP connection AND deregisters the
// VoIP push token (mirrors the regular-push unregister; the backend device row is removed by
// nativePush.unregister on the same logout). This is the ONLY path that disconnects the SIP socket and the ONLY
// path that deregisters the push token.
export const disconnectNativeTelephonyAction = () => dispatch => {
  nativeTelephonyRefs.unsubscribers.forEach(off => off());
  nativeTelephonyRefs.unsubscribers = [];
  nativeTelephonyRefs.autoAnswerCallId = null;
  nativeTelephonyRefs.connectPromise = null;

  if (getIsNativePlatform()) {
    const transport = getTelephonyTransport();

    transport
      .disconnect()
      .catch(error => logger.error('[NativeTelephony] disconnect failed', error));
    transport
      .deregisterPushToken()
      .catch(error => logger.error('[NativeTelephony] voip deregister failed', error));

    dispatch({ type: TELEPHONY_ACTION_TYPE.TELNYX_DISCONNECTED });
  }
};

export const makeOutgoingCallAction = catchAsyncActionError(
  ({ destinationNumber, callerNumber, applicationId, contactId }) =>
    async (dispatch, getState) => {
      // Fail closed on both gates the dial affordances apply, because everything below has
      // server-visible side effects that land before the transport dial can fail: presence flips to
      // ON_CALL and a scheduled-call record is written. `toDialableNumber` yields an empty
      // destination for a number of no recognized shape, and `selectCanPlaceCall` is the
      // entitlement (telephony claim + provisioned telephony user + live connection) that
      // ClickablePhone and the contact quick actions render behind — a caller that dispatches
      // without consulting it must not get further than here.
      if (!destinationNumber || !selectCanPlaceCall(getState())) {
        // Named so a dead dialer is diagnosable: the eight header/bottom-nav dispatchers render on
        // the weaker `selectIsTelnyxConnected`, so a connected socket without the claim or a loaded
        // telephony profile leaves those buttons enabled and clicking them refuses here.
        logger.info(
          '[Telnyx] Outgoing call refused:',
          destinationNumber
            ? 'caller is not entitled to place calls'
            : 'no dialable destination number',
        );

        return null;
      }

      const { user } = getState().system.telephony.telephonyUser;
      const callerName = user?.firstName
        ? `${user.firstName} ${user.lastName ?? ''}`.trim()
        : undefined;

      const extensionNumber = user?.extensionNumber ?? callerNumber;

      const clientState = btoa(
        jsonStringify({
          to: destinationNumber,
          callType: 'OutgoingWebRtcCall',
          ...getConditionalObject(!!applicationId, () => ({ applicationId })),
          ...getConditionalObject(!!contactId, () => ({ contactId })),
        }),
      );

      dispatch({
        type: TELEPHONY_ACTION_TYPE.CALL_OUTGOING,
        payload: { dialedNumber: destinationNumber },
      });

      const previousStatus = user?.contactStatus;

      dispatch({ type: TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS, payload: UserStatus.ON_CALL });

      try {
        await telephonyApi.updateUserStatus({ contactStatus: UserStatus.ON_CALL });
      } catch (error) {
        logger.warn('[Telnyx] Failed to update status to ON_CALL', error);
      }

      try {
        await telephonyApi.scheduleCall({ dialedNumber: destinationNumber, callerNumber });
      } catch (error) {
        logger.warn('[Telnyx] Schedule call failed (non-blocking)', error);
      }

      try {
        // Bare pattern (DEV-4332): on native the TxClient is already connected+REGED from the boot/login connect
        // (the SDK keeps its connection), so dial straight away — no just-in-time connect.
        // No-op consideration on web: TelnyxProvider owns the connection.
        // Web transport returns a Telnyx Call synchronously (.id); the native plugin transport returns
        // a Promise<{ callId }>. Awaiting handles both — and crucially lets a native rejection (e.g. the
        // client isn't connected) land in this catch instead of leaving the call stuck on "connecting".
        const call = await getTelephonyTransport().dial({
          destinationNumber,
          callerNumber: extensionNumber,
          callerName,
          clientState,
        });

        dispatch({
          type: TELEPHONY_ACTION_TYPE.CALL_OUTGOING,
          payload: {
            dialedNumber: destinationNumber,
            callId: call?.callId ?? call?.id,
          },
        });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_DESTROY });

        if (previousStatus) {
          dispatch({ type: TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS, payload: previousStatus });

          try {
            await telephonyApi.updateUserStatus({ contactStatus: previousStatus });
          } catch (restoreError) {
            logger.warn('[Telnyx] Failed to restore previous status', restoreError);
          }
        }

        throw error;
      }
    },
);

export const hangupCallAction = () => async (dispatch, getState) => {
  const { previousStatus } = getState().system.telephony;

  getTelephonyTransport().hangup();
  // CALL_HANGUP reducer automatically restores previousStatus in Redux state
  dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_HANGUP, payload: { cause: 'user_hangup' } });

  // Sync restored status to the backend
  if (previousStatus) {
    try {
      await telephonyApi.updateUserStatus({ contactStatus: previousStatus });
    } catch (error) {
      logger.warn('[Telnyx] Failed to restore previous status after hangup', error);
    }
  }
};

export const answerCallAction = () => () => {
  getTelephonyTransport().answer();
  // CALL_ACTIVE is dispatched by TelnyxService._handleCallStateChange when the call
  // actually transitions to 'active' — avoids premature timer start.
};

export const holdCallAction = () => dispatch => {
  getTelephonyTransport().hold(true);
  dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_HOLD });
};

export const unholdCallAction = () => dispatch => {
  getTelephonyTransport().hold(false);
  dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_UNHOLD });
};

export const muteCallAction = () => dispatch => {
  getTelephonyTransport().mute(true);
  dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_MUTE });
};

export const unmuteCallAction = () => dispatch => {
  getTelephonyTransport().mute(false);
  dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_UNMUTE });
};

export const sendDtmfAction = digit => () => {
  getTelephonyTransport().sendDtmf(digit);
};

export const saveCallAction = catchAsyncActionError(
  ({ dispositionType, note, telnyxCallId }) =>
    async dispatch => {
      await telephonyApi.saveCall({ dispositionType, note, telnyxCallId });
      dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_RESET });
    },
);

export const setDialedNumberAction = number => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_DIALED_NUMBER, payload: number });

export const fetchContactsAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_PENDING });

  try {
    const { data } = await telephonyApi.getContacts();

    return dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_FULFILLED, payload: data });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_REJECTED });
    dispatch(showErrorMessageAction({ message: 'Failed to load contacts', err: error }));
    throw error;
  }
});

export const setContactsSearchAction = query => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_CONTACTS_SEARCH, payload: query });

export const fetchCallHistoryAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_PENDING });

  try {
    const { data } = await telephonyApi.getCallHistory();

    return dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_FULFILLED, payload: data });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_REJECTED });
    dispatch(showErrorMessageAction({ message: 'Failed to load call history', err: error }));
    throw error;
  }
});

export const setCallHistoryFilterAction = filter => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_CALL_HISTORY_FILTER, payload: filter });

// SignalR connection actions
export const setSignalRConnectingAction = () => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SIGNALR_CONNECTING });

export const setSignalRConnectedAction = () => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SIGNALR_CONNECTED });

export const setSignalRDisconnectedAction = () => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SIGNALR_DISCONNECTED });

export const setSignalRReconnectingAction = () => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SIGNALR_RECONNECTING });

// Lead notification actions
export const acceptLeadAction = catchAsyncActionError(({ notificationId }) => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_PENDING });

  try {
    await telephonyApi.acceptLead({ notificationId });

    return dispatch({ type: TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_FULFILLED });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_REJECTED });
    dispatch(showErrorMessageAction({ message: 'Failed to accept lead', err: error }));
    throw error;
  }
});

export const rejectLeadAction = catchAsyncActionError(
  ({ notificationId, reason }) =>
    async dispatch => {
      dispatch({ type: TELEPHONY_ACTION_TYPE.REJECT_LEAD_PENDING });

      try {
        await telephonyApi.rejectLead({ notificationId, reason });

        return dispatch({ type: TELEPHONY_ACTION_TYPE.REJECT_LEAD_FULFILLED });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.REJECT_LEAD_REJECTED });
        dispatch(showErrorMessageAction({ message: 'Failed to reject lead', err: error }));
        throw error;
      }
    },
);

export const timeoutLeadAction = catchAsyncActionError(({ notificationId }) => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.LEAD_NOTIFICATION_TIMEOUT });

  try {
    await telephonyApi.timeoutLead({ notificationId });
  } catch (error) {
    logger.warn('[Telephony] Failed to report lead timeout', error);
  }
});

// Text messaging actions (DEV-1453)
export const fetchTextLeadsAction = catchAsyncActionError(
  ({ phoneNumber } = {}) =>
    async dispatch => {
      dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_PENDING });

      try {
        const { data } = await telephonyApi.getTextLeads({ phoneNumber });

        return dispatch({
          type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_FULFILLED,
          payload: data,
        });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_REJECTED });
        dispatch(showErrorMessageAction({ message: 'Failed to load text leads', err: error }));
        throw error;
      }
    },
);

export const setTextLeadsSearchAction = query => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_TEXT_LEADS_SEARCH, payload: query });

export const openConversationAction = catchAsyncActionError(({ lead }) => async dispatch => {
  dispatch({
    type: TELEPHONY_ACTION_TYPE.SET_ACTIVE_CONVERSATION,
    payload: lead,
  });

  dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_PENDING });

  try {
    const { data } = await telephonyApi.getTextMessages({
      phoneNumberTo: lead.phoneNumber,
    });

    return dispatch({
      type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_FULFILLED,
      payload: data,
    });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_REJECTED });
    dispatch(showErrorMessageAction({ message: 'Failed to load messages', err: error }));
    throw error;
  }
});

export const closeConversationAction = () => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.CLEAR_ACTIVE_CONVERSATION });

export const sendTextMessageAction = catchAsyncActionError(
  ({ toPhoneNumber, text }) =>
    async dispatch => {
      dispatch({ type: TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_PENDING });

      try {
        const { data } = await telephonyApi.sendTextMessage({ toPhoneNumber, text });

        return dispatch({
          type: TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_FULFILLED,
          payload: data,
        });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_REJECTED });
        dispatch(showErrorMessageAction({ message: 'Failed to send message', err: error }));
        throw error;
      }
    },
);

// Borrower chat actions (DEV-1454)
export const setBorrowerChatInitializedAction = isInitialized => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_BORROWER_CHAT_INITIALIZED, payload: isInitialized });

export const setBorrowerChatActiveChannelAction = channelUrl => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_BORROWER_CHAT_ACTIVE_CHANNEL, payload: channelUrl });

// Internal chat actions (DEV-1455)
export const setInternalChatActiveChannelAction = channelUrl => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_INTERNAL_CHAT_ACTIVE_CHANNEL, payload: channelUrl });

// Audio device settings actions (DEV-1482)
export const setAvailableAudioDevicesAction =
  ({ inputDevices, outputDevices }) =>
  dispatch =>
    dispatch({
      type: TELEPHONY_ACTION_TYPE.SET_AVAILABLE_AUDIO_DEVICES,
      payload: { inputDevices, outputDevices },
    });

export const setSelectedInputDeviceAction = deviceId => dispatch => {
  telnyxService.setInputDevice(deviceId);

  return dispatch({ type: TELEPHONY_ACTION_TYPE.SET_SELECTED_INPUT_DEVICE, payload: deviceId });
};

export const setSelectedOutputDeviceAction = deviceId => dispatch => {
  telnyxService.setOutputDevice(deviceId);

  return dispatch({ type: TELEPHONY_ACTION_TYPE.SET_SELECTED_OUTPUT_DEVICE, payload: deviceId });
};

export const setAudioPermissionStateAction = permissionState => dispatch =>
  dispatch({ type: TELEPHONY_ACTION_TYPE.SET_AUDIO_PERMISSION_STATE, payload: permissionState });

// Voicemail greeting actions (DEV-1575)
export const fetchActiveGreetingAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_PENDING });

  try {
    const { data } = await userAudioRecordingApi.getActiveGreeting();

    return dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_FULFILLED, payload: data });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_REJECTED });
    throw error;
  }
});

export const setActiveGreetingAction = catchAsyncActionError(
  ({ audioRecordingId }) =>
    async dispatch => {
      dispatch({ type: TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_PENDING });

      try {
        await userAudioRecordingApi.setActiveGreeting({ audioRecordingId });

        return dispatch({
          type: TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_FULFILLED,
          payload: audioRecordingId,
        });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_REJECTED });
        dispatch(
          showErrorMessageAction({ message: 'Failed to set voicemail greeting', err: error }),
        );
        throw error;
      }
    },
);

export const removeActiveGreetingAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_PENDING });

  try {
    await userAudioRecordingApi.removeActiveGreeting();

    return dispatch({ type: TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_FULFILLED });
  } catch (error) {
    dispatch({ type: TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_REJECTED });
    dispatch(
      showErrorMessageAction({ message: 'Failed to remove voicemail greeting', err: error }),
    );
    throw error;
  }
});

// Voicemail inbox actions (DEV-1577)
export const fetchVoicemailsAction = catchAsyncActionError(
  ({ page, pageSize } = {}) =>
    async dispatch => {
      dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_PENDING });

      try {
        const { data } = await telephonyApi.getVoicemails({ page, pageSize });

        return dispatch({
          type: TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_FULFILLED,
          payload: data,
        });
      } catch (error) {
        dispatch({ type: TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_REJECTED });
        dispatch(showErrorMessageAction({ message: 'Failed to load voicemails', err: error }));
        throw error;
      }
    },
);

export const fetchVoicemailUnreadCountAction = catchAsyncActionError(() => async dispatch => {
  try {
    const { data } = await telephonyApi.getVoicemailUnreadCount();

    return dispatch({
      type: TELEPHONY_ACTION_TYPE.FETCH_VOICEMAIL_UNREAD_COUNT_FULFILLED,
      payload: data,
    });
  } catch (error) {
    logger.warn('[Telephony] Failed to fetch voicemail unread count', error);
  }
});

export const markVoicemailReadAction = catchAsyncActionError(
  ({ voicemailId }) =>
    async dispatch => {
      await telephonyApi.markVoicemailRead({ voicemailId });

      return dispatch({
        type: TELEPHONY_ACTION_TYPE.MARK_VOICEMAIL_READ_FULFILLED,
        payload: voicemailId,
      });
    },
);

export const markAllVoicemailsReadAction = catchAsyncActionError(() => async dispatch => {
  await telephonyApi.markAllVoicemailsRead();

  return dispatch({ type: TELEPHONY_ACTION_TYPE.MARK_ALL_VOICEMAILS_READ_FULFILLED });
});

export const deleteVoicemailAction = catchAsyncActionError(({ voicemailId }) => async dispatch => {
  await telephonyApi.deleteVoicemail({ voicemailId });

  return dispatch({
    type: TELEPHONY_ACTION_TYPE.DELETE_VOICEMAIL_FULFILLED,
    payload: voicemailId,
  });
});
